import {
	Button,
	DrawerFooter,
	Input,
	Label,
	Separator,
	Textarea,
} from "@monbits/ui";
import {
	CheckCircle2,
	CreditCard,
	Link as LinkIcon,
	Loader2,
	Wallet,
} from "lucide-react";
import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import CopyButton from "src/components/copy-button/copy-button";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { EntityDrawer } from "src/components/entity-drawer";
import { StatusBadge, type StatusType } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import type {
	EligiblePaymentCandidate,
	ExpiredOrderCandidate,
	OrphanCreditEstimate,
	OrphanTransactionDetail,
} from "src/providers/orphan-transaction-provider";
import {
	useCreditOrphanToMerchant,
	useLinkOrphanToOrder,
	useLinkOrphanToPayment,
	useOrphanCreditEstimate,
	useOrphanTransaction,
} from "src/queries/orphan-transaction-queries";
import { MerchantPicker, type PickerMerchant } from "./merchant-picker";

type Destination = "order" | "payment" | "merchant";
type Step = "choose" | "confirm" | "success";

const truncate = (s: string, head = 8, tail = 6) =>
	s.length <= head + tail ? s : `${s.slice(0, head)}...${s.slice(-tail)}`;

const paymentStatusType = (status: string): StatusType => {
	switch (status) {
		case "success":
			return "success";
		case "failed":
		case "aml_rejected":
			return "error";
		case "pending":
		case "locked":
			return "warning";
		case "inProgress":
			return "info";
		default:
			return "default";
	}
};

const UnattributedDepositDetail: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const orphanId = id ? Number(id) : null;

	const [step, setStep] = React.useState<Step>("choose");
	const [destination, setDestination] = React.useState<Destination>("merchant");
	const [selectedOrderUuid, setSelectedOrderUuid] = React.useState("");
	const [selectedPaymentUuid, setSelectedPaymentUuid] = React.useState("");
	const [selectedMerchant, setSelectedMerchant] =
		React.useState<PickerMerchant | null>(null);
	const [reason, setReason] = React.useState("");
	const [reasonTouched, setReasonTouched] = React.useState(false);
	const [totpCode, setTotpCode] = React.useState("");
	const [assigned, setAssigned] = React.useState(false);
	const [result, setResult] = React.useState<{
		merchantName: string;
		creditedAmount: string;
		serviceFee: string;
		currency: string;
	} | null>(null);

	const detailQuery = useOrphanTransaction(orphanId, !assigned);
	const detail = detailQuery.data;

	const linkOrder = useLinkOrphanToOrder();
	const linkPayment = useLinkOrphanToPayment();
	const creditMerchant = useCreditOrphanToMerchant();

	const orders = detail?.expiredOrders ?? [];
	const payments = detail?.eligiblePayments ?? [];

	const backTo = React.useMemo(() => {
		const qs = searchParams.toString();
		return `/unattributed-deposits${qs ? `?${qs}` : ""}`;
	}, [searchParams]);

	React.useEffect(() => {
		if (!detail) return;
		setDestination(
			detail.expiredOrders.length > 0
				? "order"
				: detail.eligiblePayments.length > 0
					? "payment"
					: "merchant",
		);
		setSelectedMerchant(
			detail.suggestion
				? {
						uuid: detail.suggestion.merchantUuid,
						name: detail.suggestion.merchantName,
					}
				: null,
		);
	}, [detail]);

	const estimateQuery = useOrphanCreditEstimate(
		destination === "merchant" && selectedMerchant && !assigned
			? orphanId
			: null,
		selectedMerchant?.uuid ?? "",
	);
	const liveEstimate = estimateQuery.data;
	const estimateError = estimateQuery.error as
		| { response?: { data?: { message?: string } } }
		| null
		| undefined;

	const isPending =
		linkOrder.isPending || linkPayment.isPending || creditMerchant.isPending;

	const targetReady =
		(destination === "order" && !!selectedOrderUuid) ||
		(destination === "payment" && !!selectedPaymentUuid) ||
		(destination === "merchant" && !!selectedMerchant && !!liveEstimate);

	const plan = React.useMemo(() => {
		if (!detail) return null;
		if (destination === "order") {
			const o = orders.find((x) => x.orderUuid === selectedOrderUuid);
			if (!o) return null;
			return {
				merchantName: o.merchantName,
				creditedAmount: detail.netAmountAfterFee,
				serviceFee: detail.estimatedServiceFee,
				feeRatePct: (detail.estimatedFeeRate * 100).toFixed(2),
				estimated: true,
				effect: `Order ${o.merchantOrderId || `#${o.orderId}`} will be marked paid.`,
			};
		}
		if (destination === "payment") {
			const p = payments.find((x) => x.paymentUuid === selectedPaymentUuid);
			if (!p) return null;
			return {
				merchantName: p.merchantName,
				creditedAmount: detail.netAmountAfterFee,
				serviceFee: detail.estimatedServiceFee,
				feeRatePct: (detail.estimatedFeeRate * 100).toFixed(2),
				estimated: true,
				effect: `Payment ${
					p.merchantOrderId || truncate(p.paymentUuid, 8, 6)
				} will be marked successful.`,
			};
		}
		if (!selectedMerchant || !liveEstimate) return null;
		return {
			merchantName: selectedMerchant.name,
			creditedAmount: liveEstimate.merchantCredit,
			serviceFee: liveEstimate.serviceFee,
			feeRatePct: (liveEstimate.feeRate * 100).toFixed(2),
			estimated: false,
			effect: "No order or payment is modified. This is a manual adjustment.",
		};
	}, [
		detail,
		destination,
		orders,
		payments,
		selectedOrderUuid,
		selectedPaymentUuid,
		selectedMerchant,
		liveEstimate,
	]);

	const reasonValid = reason.trim().length >= 10;
	const totpValid = totpCode.length === 6;
	const canConfirm = !!plan && reasonValid && totpValid && !isPending;

	const handleConfirm = async () => {
		if (orphanId === null || !plan) return;
		const trimmedReason = reason.trim();
		try {
			if (destination === "order") {
				await linkOrder.mutateAsync({
					id: orphanId,
					data: {
						orderUuid: selectedOrderUuid,
						reason: trimmedReason,
						totpCode,
					},
				});
			} else if (destination === "payment") {
				await linkPayment.mutateAsync({
					id: orphanId,
					data: {
						paymentUuid: selectedPaymentUuid,
						reason: trimmedReason,
						totpCode,
					},
				});
			} else {
				await creditMerchant.mutateAsync({
					id: orphanId,
					data: {
						merchantUuid: selectedMerchant?.uuid ?? "",
						reason: trimmedReason,
						totpCode,
					},
				});
			}
			setResult({
				merchantName: plan.merchantName,
				creditedAmount: plan.creditedAmount,
				serviceFee: plan.serviceFee,
				currency: detail?.currency ?? "",
			});
			setAssigned(true);
			setStep("success");
		} catch (error) {
			const message =
				(error as { response?: { data?: { message?: string } } })?.response
					?.data?.message || "Failed to assign deposit";
			toast.error(message);
		}
	};

	const body =
		step === "success" && result ? (
			<SuccessBody result={result} />
		) : !detail ? null : (
			<div className="space-y-6 p-6">
				<div className="flex items-center gap-3">
					<CryptoIcon ticker={detail.currency} className="h-9 w-9 shrink-0" />
					<div className="min-w-0">
						<div className="font-mono text-lg font-semibold tabular-nums leading-tight">
							{detail.factAmount ?? detail.amount} {detail.currency}
						</div>
						<div className="mt-0.5 text-xs text-muted-foreground">
							{detail.blockchain}
							{detail.isTest ? " · testnet" : ""} · received{" "}
							<TimeLabel time={detail.createdAt} />
						</div>
					</div>
				</div>

				<div className="space-y-3">
					<h3 className="text-sm font-semibold">Transaction</h3>
					<div className="space-y-3">
						<CopyField label="Recipient" value={detail.recipientAddress} />
						{detail.senderAddress && (
							<CopyField label="Sender" value={detail.senderAddress} />
						)}
						{detail.transactionHash && (
							<CopyField label="Hash" value={detail.transactionHash} />
						)}
						{detail.suggestion && (
							<Field label="Suggested merchant">
								<span className="text-sm font-medium">
									{detail.suggestion.merchantName}
								</span>
							</Field>
						)}
					</div>
				</div>

				<Separator />

				<div className="space-y-4">
					<h3 className="text-sm font-semibold">
						{step === "choose" ? "Choose destination" : "Review and confirm"}
					</h3>

					{step === "choose" ? (
						<ChooseDestination
							detail={detail}
							destination={destination}
							onDestination={setDestination}
							selectedOrderUuid={selectedOrderUuid}
							onSelectOrder={setSelectedOrderUuid}
							selectedPaymentUuid={selectedPaymentUuid}
							onSelectPayment={setSelectedPaymentUuid}
							selectedMerchant={selectedMerchant}
							onSelectMerchant={setSelectedMerchant}
							estimateLoading={estimateQuery.isFetching}
							estimate={liveEstimate}
							estimateError={estimateError?.response?.data?.message}
							disabled={isPending}
						/>
					) : plan ? (
						<ConfirmAssignment
							detail={detail}
							plan={plan}
							reason={reason}
							reasonError={reasonTouched && !reasonValid}
							onReason={setReason}
							onReasonBlur={() => setReasonTouched(true)}
							totpCode={totpCode}
							onTotp={setTotpCode}
							disabled={isPending}
						/>
					) : null}
				</div>
			</div>
		);

	const footer =
		step === "success" ? (
			<Button className="w-full" onClick={() => navigate(backTo)}>
				Back to deposits
			</Button>
		) : step === "choose" ? (
			<div className="flex gap-2">
				<Button
					variant="outline"
					className="flex-1"
					onClick={() => navigate(backTo)}
				>
					Cancel
				</Button>
				<Button
					className="flex-1"
					onClick={() => setStep("confirm")}
					disabled={!targetReady}
				>
					Continue
				</Button>
			</div>
		) : (
			<div className="flex gap-2">
				<Button
					variant="outline"
					className="flex-1"
					onClick={() => setStep("choose")}
					disabled={isPending}
				>
					Back
				</Button>
				<Button
					className="flex-1"
					onClick={handleConfirm}
					disabled={!canConfirm}
				>
					{isPending ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							Crediting...
						</>
					) : (
						"Confirm and credit"
					)}
				</Button>
			</div>
		);

	return (
		<EntityDrawer
			title={`Deposit #${detail?.id ?? id ?? ""}`}
			isLoading={!assigned && detailQuery.isLoading}
			isNotFound={!assigned && !detail && !detailQuery.isLoading}
			notFoundMessage="Deposit not available"
			parentPath="/unattributed-deposits"
		>
			{/* Fill the drawer height so the body scrolls and the footer action
			    stays pinned and thumb-reachable on a full-screen mobile drawer. */}
			<div className="flex h-full flex-col">
				<div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
				<Separator />
				<DrawerFooter className="flex-shrink-0 p-4">{footer}</DrawerFooter>
			</div>
		</EntityDrawer>
	);
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
	label,
	children,
}) => (
	<div>
		<div className="mb-1 text-xs font-medium text-muted-foreground">
			{label}
		</div>
		{children}
	</div>
);

const CopyField: React.FC<{ label: string; value: string }> = ({
	label,
	value,
}) => (
	<Field label={label}>
		<div className="flex items-center gap-2">
			<span className="flex-1 break-all font-mono text-xs">{value}</span>
			<CopyButton value={value} label={label} />
		</div>
	</Field>
);

interface ChooseDestinationProps {
	detail: OrphanTransactionDetail;
	destination: Destination;
	onDestination: (d: Destination) => void;
	selectedOrderUuid: string;
	onSelectOrder: (uuid: string) => void;
	selectedPaymentUuid: string;
	onSelectPayment: (uuid: string) => void;
	selectedMerchant: PickerMerchant | null;
	onSelectMerchant: (m: PickerMerchant) => void;
	estimateLoading: boolean;
	estimate: OrphanCreditEstimate | undefined;
	estimateError: string | undefined;
	disabled: boolean;
}

const ChooseDestination: React.FC<ChooseDestinationProps> = ({
	detail,
	destination,
	onDestination,
	selectedOrderUuid,
	onSelectOrder,
	selectedPaymentUuid,
	onSelectPayment,
	selectedMerchant,
	onSelectMerchant,
	estimateLoading,
	estimate,
	estimateError,
	disabled,
}) => {
	const orders = detail.expiredOrders;
	const payments = detail.eligiblePayments;

	const options = React.useMemo(
		() => [
			{
				key: "order" as const,
				icon: <LinkIcon className="h-4 w-4" />,
				title: "Expired order",
				hint: orders.length
					? `${orders.length} match${orders.length > 1 ? "es" : ""} at this address`
					: "No matched orders",
				disabled: orders.length === 0,
			},
			{
				key: "payment" as const,
				icon: <CreditCard className="h-4 w-4" />,
				title: "Payment",
				hint: payments.length
					? `${payments.length} match${payments.length > 1 ? "es" : ""} at this address`
					: "No matched payments",
				disabled: payments.length === 0,
			},
			{
				key: "merchant" as const,
				icon: <Wallet className="h-4 w-4" />,
				title: "Merchant",
				hint: "Credit directly, no order or payment changed",
				disabled: false,
			},
		],
		[orders.length, payments.length],
	);

	const optionRefs = React.useRef<
		Record<Destination, HTMLButtonElement | null>
	>({ order: null, payment: null, merchant: null });

	const handleKeyDown = (e: React.KeyboardEvent) => {
		const enabled = options.filter((o) => !o.disabled).map((o) => o.key);
		const idx = enabled.indexOf(destination);
		if (idx === -1) return;
		let next: Destination | null = null;
		if (e.key === "ArrowDown" || e.key === "ArrowRight") {
			next = enabled[(idx + 1) % enabled.length];
		} else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
			next = enabled[(idx - 1 + enabled.length) % enabled.length];
		}
		if (next) {
			e.preventDefault();
			onDestination(next);
			optionRefs.current[next]?.focus();
		}
	};

	return (
		<div className="space-y-4">
			<div
				role="radiogroup"
				aria-label="Destination"
				className="grid gap-2"
				onKeyDown={handleKeyDown}
			>
				{options.map((o) => {
					const selected = destination === o.key;
					return (
						<button
							key={o.key}
							ref={(el) => {
								optionRefs.current[o.key] = el;
							}}
							type="button"
							role="radio"
							aria-checked={selected}
							tabIndex={selected ? 0 : -1}
							disabled={o.disabled}
							onClick={() => onDestination(o.key)}
							className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${
								selected ? "border-primary bg-primary/5" : "hover:bg-muted/50"
							}`}
						>
							<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
								{o.icon}
							</span>
							<span className="min-w-0 flex-1">
								<span className="block text-sm font-medium">{o.title}</span>
								<span className="block text-xs text-muted-foreground">
									{o.hint}
								</span>
							</span>
							<span
								className={`h-4 w-4 shrink-0 rounded-full border ${
									selected
										? "border-primary bg-primary"
										: "border-muted-foreground/30"
								}`}
							/>
						</button>
					);
				})}
			</div>

			{destination === "order" && (
				<div className="space-y-2">
					<Label>Order to mark paid</Label>
					<div className="max-h-[280px] overflow-y-auto rounded-md border">
						<OrderPicker
							orders={orders}
							selectedOrderUuid={selectedOrderUuid}
							onSelect={onSelectOrder}
						/>
					</div>
				</div>
			)}

			{destination === "payment" && (
				<div className="space-y-2">
					<Label>Payment to settle</Label>
					<div className="max-h-[280px] overflow-y-auto rounded-md border">
						<PaymentPicker
							payments={payments}
							selectedPaymentUuid={selectedPaymentUuid}
							onSelect={onSelectPayment}
						/>
					</div>
				</div>
			)}

			{destination === "merchant" && (
				<div className="space-y-3">
					<div className="space-y-2">
						<Label>Merchant</Label>
						<MerchantPicker
							value={selectedMerchant}
							onChange={onSelectMerchant}
							suggestion={
								detail.suggestion
									? {
											uuid: detail.suggestion.merchantUuid,
											name: detail.suggestion.merchantName,
										}
									: null
							}
							disabled={disabled}
						/>
					</div>
					<EstimatePanel
						hasMerchant={!!selectedMerchant}
						isLoading={estimateLoading}
						estimate={estimate}
						errorMessage={estimateError}
						currency={detail.currency}
					/>
				</div>
			)}
		</div>
	);
};

interface AssignmentPlan {
	merchantName: string;
	creditedAmount: string;
	serviceFee: string;
	feeRatePct: string;
	estimated: boolean;
	effect: string;
}

interface ConfirmAssignmentProps {
	detail: OrphanTransactionDetail;
	plan: AssignmentPlan;
	reason: string;
	reasonError: boolean;
	onReason: (v: string) => void;
	onReasonBlur: () => void;
	totpCode: string;
	onTotp: (v: string) => void;
	disabled: boolean;
}

const ConfirmAssignment: React.FC<ConfirmAssignmentProps> = ({
	detail,
	plan,
	reason,
	reasonError,
	onReason,
	onReasonBlur,
	totpCode,
	onTotp,
	disabled,
}) => (
	<div className="space-y-4">
		<div className="rounded-lg bg-muted/50 p-4">
			<div className="flex items-baseline justify-between gap-3">
				<span className="truncate font-medium">{plan.merchantName}</span>
				<span className="shrink-0 font-mono text-lg font-semibold tabular-nums">
					{plan.creditedAmount} {detail.currency}
				</span>
			</div>
			<dl className="mt-3 space-y-1.5 text-sm">
				<div className="flex justify-between gap-4">
					<dt className="text-muted-foreground">Service fee</dt>
					<dd className="font-mono tabular-nums">
						{plan.serviceFee} {detail.currency} ({plan.feeRatePct}%)
					</dd>
				</div>
				<div className="flex justify-between gap-4">
					<dt className="shrink-0 text-muted-foreground">Effect</dt>
					<dd className="min-w-0 break-words text-right">{plan.effect}</dd>
				</div>
			</dl>
		</div>

		{plan.estimated && (
			<p className="text-xs text-muted-foreground">
				The amount is estimated at the suggested merchant's rate. The final
				split uses the linked entity's own fee rate.
			</p>
		)}

		<div className="space-y-2">
			<Label htmlFor="reason">
				Reason <span className="text-destructive">*</span>
			</Label>
			<Textarea
				id="reason"
				autoFocus
				placeholder="Why are you assigning this deposit? (min 10 characters)"
				value={reason}
				onChange={(e) => onReason(e.target.value)}
				onBlur={onReasonBlur}
				disabled={disabled}
				aria-invalid={reasonError}
				aria-describedby={reasonError ? "reason-error" : undefined}
				className="min-h-[90px] resize-none"
				maxLength={500}
			/>
			{reasonError ? (
				<p id="reason-error" className="text-xs text-destructive">
					Add at least 10 characters so the audit log is meaningful.
				</p>
			) : (
				<p className="text-xs text-muted-foreground">{reason.length}/500</p>
			)}
		</div>

		<div className="space-y-2">
			<Label htmlFor="totp">
				2FA Code <span className="text-destructive">*</span>
			</Label>
			<Input
				id="totp"
				type="text"
				placeholder="Enter your 6-digit 2FA code"
				value={totpCode}
				onChange={(e) => onTotp(e.target.value.replace(/\D/g, ""))}
				disabled={disabled}
				maxLength={6}
				pattern="[0-9]{6}"
				inputMode="numeric"
				autoComplete="one-time-code"
			/>
			<p className="text-xs text-muted-foreground">
				From your authenticator app.
			</p>
		</div>

		<div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-950">
			<p className="text-sm text-yellow-800 dark:text-yellow-200">
				<strong>This moves real funds and cannot be undone.</strong> The credit
				is logged in the audit trail for compliance.
			</p>
		</div>
	</div>
);

const SuccessBody: React.FC<{
	result: {
		merchantName: string;
		creditedAmount: string;
		serviceFee: string;
		currency: string;
	};
}> = ({ result }) => (
	<div className="flex flex-col items-center px-6 py-10 text-center">
		<div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-600/10">
			<CheckCircle2 className="h-6 w-6 text-green-600" />
		</div>
		<h3 className="mt-4 text-lg font-semibold">Deposit assigned</h3>
		<p className="mt-1 text-sm text-muted-foreground">
			{result.merchantName} was credited.
		</p>
		<div className="mt-6 w-full max-w-sm rounded-lg bg-muted/50 p-4 text-sm">
			<div className="flex items-center justify-between gap-4">
				<span className="text-muted-foreground">Credited</span>
				<span className="font-mono font-semibold tabular-nums">
					{result.creditedAmount} {result.currency}
				</span>
			</div>
			<div className="mt-2 flex items-center justify-between gap-4">
				<span className="text-muted-foreground">Service fee</span>
				<span className="font-mono tabular-nums">
					{result.serviceFee} {result.currency}
				</span>
			</div>
		</div>
	</div>
);

interface RadioListProps<T> {
	items: T[];
	getKey: (item: T) => string;
	selectedKey: string;
	onSelect: (key: string) => void;
	ariaLabel: string;
	emptyMessage: string;
	renderItem: (item: T) => React.ReactNode;
}

// Single-select list with the WAI-ARIA radiogroup keyboard model (roving
// tabindex + arrow keys), so screen-reader and keyboard users get the same
// single-choice semantics the visual selection implies.
function RadioList<T>({
	items,
	getKey,
	selectedKey,
	onSelect,
	ariaLabel,
	emptyMessage,
	renderItem,
}: RadioListProps<T>) {
	const refs = React.useRef<Record<string, HTMLButtonElement | null>>({});

	if (items.length === 0) {
		return (
			<div className="p-3 text-sm text-muted-foreground">{emptyMessage}</div>
		);
	}

	const keys = items.map(getKey);
	const activeKey = keys.includes(selectedKey) ? selectedKey : keys[0];

	const onKeyDown = (e: React.KeyboardEvent) => {
		const idx = keys.indexOf(activeKey);
		let next: string | null = null;
		if (e.key === "ArrowDown" || e.key === "ArrowRight") {
			next = keys[(idx + 1) % keys.length];
		} else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
			next = keys[(idx - 1 + keys.length) % keys.length];
		}
		if (next) {
			e.preventDefault();
			onSelect(next);
			refs.current[next]?.focus();
		}
	};

	return (
		<div
			role="radiogroup"
			aria-label={ariaLabel}
			className="divide-y"
			onKeyDown={onKeyDown}
		>
			{items.map((item) => {
				const key = getKey(item);
				const selected = key === selectedKey;
				return (
					<button
						key={key}
						ref={(el) => {
							refs.current[key] = el;
						}}
						type="button"
						role="radio"
						aria-checked={selected}
						tabIndex={key === activeKey ? 0 : -1}
						onClick={() => onSelect(key)}
						className={`w-full p-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
							selected ? "bg-muted" : ""
						}`}
					>
						<div className="flex items-start gap-2">
							<span
								className={`mt-1 h-3 w-3 shrink-0 rounded-full border ${
									selected
										? "border-primary bg-primary"
										: "border-muted-foreground/30"
								}`}
							/>
							<div className="min-w-0 flex-1">{renderItem(item)}</div>
						</div>
					</button>
				);
			})}
		</div>
	);
}

interface OrderPickerProps {
	orders: ExpiredOrderCandidate[];
	selectedOrderUuid: string;
	onSelect: (uuid: string) => void;
}

const OrderPicker: React.FC<OrderPickerProps> = ({
	orders,
	selectedOrderUuid,
	onSelect,
}) => (
	<RadioList
		items={orders}
		getKey={(o) => o.orderUuid}
		selectedKey={selectedOrderUuid}
		onSelect={onSelect}
		ariaLabel="Order to mark paid"
		emptyMessage="No expired orders at this address."
		renderItem={(order) => (
			<>
				<div className="flex items-baseline justify-between gap-2">
					<span className="truncate text-sm font-medium">
						{order.merchantOrderId || `#${order.orderId}`}
					</span>
					<span className="shrink-0 font-mono text-sm tabular-nums">
						{order.cryptoAmount} {order.cryptoCurrency}
					</span>
				</div>
				<div className="mt-0.5 flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
					<span className="truncate">{order.merchantName}</span>
					<span className="shrink-0 tabular-nums">
						{order.fiatPrice} {order.fiatCurrency}
					</span>
				</div>
			</>
		)}
	/>
);

interface PaymentPickerProps {
	payments: EligiblePaymentCandidate[];
	selectedPaymentUuid: string;
	onSelect: (uuid: string) => void;
}

const PaymentPicker: React.FC<PaymentPickerProps> = ({
	payments,
	selectedPaymentUuid,
	onSelect,
}) => (
	<RadioList
		items={payments}
		getKey={(p) => p.paymentUuid}
		selectedKey={selectedPaymentUuid}
		onSelect={onSelect}
		ariaLabel="Payment to settle"
		emptyMessage="No eligible payments at this address."
		renderItem={(payment) => (
			<>
				<div className="flex items-baseline justify-between gap-2">
					<span className="truncate text-sm font-medium">
						{payment.merchantOrderId || truncate(payment.paymentUuid, 8, 6)}
					</span>
					<span className="shrink-0 font-mono text-sm tabular-nums">
						{payment.fiatPrice} {payment.fiatCurrency}
					</span>
				</div>
				<div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
					<span className="truncate">{payment.merchantName}</span>
					<StatusBadge
						type={paymentStatusType(payment.status)}
						className="shrink-0 text-xs"
					>
						{payment.status}
					</StatusBadge>
				</div>
			</>
		)}
	/>
);

interface EstimatePanelProps {
	hasMerchant: boolean;
	isLoading: boolean;
	estimate: OrphanCreditEstimate | undefined;
	errorMessage: string | undefined;
	currency: string;
}

const EstimatePanel: React.FC<EstimatePanelProps> = ({
	hasMerchant,
	isLoading,
	estimate,
	errorMessage,
	currency,
}) => {
	if (!hasMerchant) {
		return (
			<div className="rounded-lg border border-dashed bg-muted/30 p-3 text-sm text-muted-foreground">
				Select a merchant to preview the credit.
			</div>
		);
	}
	if (isLoading && !estimate) {
		return (
			<div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
				Loading preview…
			</div>
		);
	}
	if (errorMessage) {
		return (
			<div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
				{errorMessage}
			</div>
		);
	}
	if (!estimate) return null;
	return (
		<div className="space-y-2 rounded-lg bg-primary/5 p-3 text-sm">
			<div className="flex items-baseline justify-between gap-2">
				<span className="text-muted-foreground">Fee rate</span>
				<span className="font-mono tabular-nums">
					{(estimate.feeRate * 100).toFixed(2)}%
				</span>
			</div>
			<div className="flex items-baseline justify-between gap-2">
				<span className="text-muted-foreground">Service fee</span>
				<span className="font-mono tabular-nums">
					{estimate.serviceFee} {currency}
				</span>
			</div>
			<div className="mt-2 flex items-baseline justify-between gap-2 border-t pt-2">
				<span className="font-medium">Will credit</span>
				<span className="font-mono font-semibold tabular-nums">
					{estimate.merchantCredit} {currency}
				</span>
			</div>
		</div>
	);
};

export default UnattributedDepositDetail;
